import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { franchiseAPI } from '../../services/api';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function FranchisesAdmin() {
  const { user } = useAuth();
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', address: '', phone: '', email: '' });

  useEffect(() => { fetchFranchises(); }, []);

  const fetchFranchises = async () => {
    try {
      const { data } = await franchiseAPI.getAll();
      setFranchises(data);
    } catch (error) {
      toast.error('Failed to load franchises');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await franchiseAPI.create(formData);
      toast.success('Franchise created successfully');
      setShowForm(false);
      setFormData({ name: '', address: '', phone: '', email: '' });
      fetchFranchises();
    } catch (error) {
      toast.error('Failed to create franchise');
    }
  };

  return (
    <AdminLayout activeItem="Franchises">
      <header className="bg-white border-b border-slate-200 px-8 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-medium text-brand-primary font-outfit">Franchises</h1>
          {user?.role === 'super_admin' && (
            <button
              onClick={() => setShowForm(true)}
              className="flex items-center bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200"
              data-testid="add-franchise-button"
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Franchise
            </button>
          )}
        </div>
      </header>

      <main className="p-8">
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="franchise-form-modal"
            onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}>
            <div className="bg-white p-8 max-w-md w-full mx-4 border border-slate-200">
              <h2 className="text-xl font-medium text-brand-primary mb-6">Add Franchise</h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Franchise Name</label>
                  <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent" data-testid="franchise-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                  <textarea required value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={2} className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent" data-testid="franchise-address-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                  <input type="tel" required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent" data-testid="franchise-phone-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input type="email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 focus:outline-none focus:ring-1 focus:ring-brand-accent" data-testid="franchise-email-input" />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 bg-brand-accent text-white hover:bg-brand-accent-hover px-6 py-2 shadow-sm transition-all duration-200" data-testid="franchise-submit-button">
                    Create
                  </button>
                  <button type="button" onClick={() => setShowForm(false)} className="flex-1 border border-slate-300 text-slate-700 hover:bg-slate-50 px-6 py-2 transition-all duration-200">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-accent"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {franchises.map((franchise) => (
              <div key={franchise.id} className="bg-white p-6 border border-slate-200 hover:shadow-md transition-all duration-200" data-testid={`franchise-card-${franchise.id}`}>
                <div className="flex items-start space-x-4">
                  <Building2 className="h-10 w-10 text-brand-accent flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-medium text-brand-primary">{franchise.name}</h3>
                    <p className="text-sm text-slate-600 mt-1">{franchise.address}</p>
                    <p className="text-sm text-slate-500 mt-2">{franchise.phone}</p>
                    <p className="text-sm text-brand-accent mt-1">{franchise.email}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </AdminLayout>
  );
}
